<script setup lang="ts">
    import { Alert } from '@/components/ui/alert';
    import { computed, ref } from 'vue';
    import { Input } from '@/components/ui/input';
    import { InputRow, Select, Combobox } from '@/components/ui/form';
    import EmployeeOptionalInput from '@/components/employees/EmployeeOptionalInput.vue';
    import FileView from '@/components/FileView.vue';
    import ThousandSeparatorInput from '@/components/ThousandSeparatorInput.vue';
    import type { Company, Employee, FileItem } from '@/types';

    const props = defineProps<{
        form: any;
        company: Company;
        danmarksStatistikData: any;
        employee?: Employee;
    }>();

    const dstValuesOptions = computed(() => {
        const types = props.danmarksStatistikData?.dstValues?.employmentTypes ?? [];

        return Object.entries(types).map(([key, label]) => ({
            value: key,
            label: label as string,
        }));
    });

    const hiredTermsOptions = computed(() => {
        const types = props.danmarksStatistikData?.dstValues?.hiredTerms ?? [];

        return Object.entries(types).map(([key, label]) => ({
            value: key,
            label: label as string,
        }));
    });

    const jobStatusesOptions = computed(() => {
        const types = props.danmarksStatistikData?.dstValues?.jobStatuses ?? [];

        return Object.entries(types).map(([key, label]) => ({
            value: key,
            label: label as string,
        }));
    });

    const salaryFormsOptions = computed(() => {
        const types = props.danmarksStatistikData?.dstValues?.salaryForms ?? [];

        return Object.entries(types).map(([key, label]) => ({
            value: key,
            label: label as string,
        }));
    });

    const discoCodesOptions = computed(() => {
        const types = props.danmarksStatistikData?.discoCodes ?? [];

        return Object.entries(types).map(([key, label]) => ({
            value: key,
            label: label as string,
        }));
    });

    const carFile = ref<FileItem | null>({
        name: props.employee?.employment?.free_car_filename,
        url: route('v2.salary.employees.download-free-car-contract', props.employee?.id),
    } as FileItem);

    const deleteCarFile = () => {
        props.form.employment.remove_free_car_file = true;
        carFile.value = null;
    };
</script>

<template>
    <div class="grid gap-3">
        <EmployeeOptionalInput
            v-if="company.employees_optional.has_free_car"
            :form="form"
            label="employee.optional.has_free_car"
            value="employment.has_free_car"
        >
            <InputRow
                :form="form"
                name="free_car_monthly"
                label="employee.employment.free_car_monthly"
            >
                <ThousandSeparatorInput
                    v-model="form.employment.free_car_monthly"
                    :placeholder="$t('employee.employment.free_car_monthly')"
                    with-decimals
                />
            </InputRow>
            <InputRow
                :form="form"
                name="free_car_file"
                label="employee.employment.free_car_file"
            >
                <Input
                    id="free_car_file"
                    @input="form.employment.free_car_file = $event.target.files[0]"
                    :placeholder="$t('employee.employment.free_car_file')"
                    type="file"
                    accept="image/png,image/gif,image/jpeg,application/pdf,.xlsx,.xls,.csv"
                />
            </InputRow>
            <FileView
                v-if="carFile?.name"
                :file="carFile"
                :previewable="false"
                @delete="deleteCarFile"
            />
            <InputRow
                :form="form"
                name="free_car_file_description"
                label="employee.employment.free_car_file_description"
            >
                <Input
                    id="free_car_file_description"
                    v-model="form.employment.free_car_file_description"
                    :placeholder="$t('employee.employment.free_car_file_description')"
                />
            </InputRow>
            <Alert variant="warning">
                {{ $t('employee.employment.free_car_file_description_warning') }}
            </Alert>
        </EmployeeOptionalInput>

        <EmployeeOptionalInput
            v-if="company.employees_optional.kilometer"
            :form="form"
            label="employee.employment.kilometer"
            value="employment.kilometer"
        />

        <EmployeeOptionalInput
            v-if="company.employees_optional.has_free_phone"
            :form="form"
            label="employee.employment.has_free_phone"
            value="employment.has_free_phone"
        />

        <EmployeeOptionalInput
            v-if="company.employees_optional.has_lunch_arrangement"
            :form="form"
            label="employee.employment.has_lunch_arrangement"
            value="employment.has_lunch_arrangement"
        >
            <InputRow
                :form="form"
                name="lunch_arrangement_monthly"
                label="employee.employment.lunch_arrangement_monthly"
                error-key="employment.lunch_arrangement_monthly"
            >
                <ThousandSeparatorInput
                    v-model="form.employment.lunch_arrangement_monthly"
                    :placeholder="$t('employee.employment.lunch_arrangement_monthly')"
                    with-decimals
                />
            </InputRow>
        </EmployeeOptionalInput>

        <EmployeeOptionalInput
            v-if="company.employees_optional.has_fixed_add_ons"
            :form="form"
            label="employee.employment.has_fixed_add_ons"
            value="employment.has_fixed_add_ons"
        >
            <InputRow
                :form="form"
                name="fixed_add_ons_monthly"
                label="employee.employment.fixed_add_ons_monthly"
                error-key="employment.fixed_add_ons_monthly"
            >
                <ThousandSeparatorInput
                    v-model="form.employment.fixed_add_ons_monthly"
                    :placeholder="$t('employee.employment.fixed_add_ons_monthly')"
                    with-decimals
                />
            </InputRow>
        </EmployeeOptionalInput>

        <EmployeeOptionalInput
            v-if="company.employees_optional.has_pension"
            :form="form"
            label="employee.employment.has_pension"
            value="employment.has_pension"
        />

        <EmployeeOptionalInput
            v-if="company.employees_optional.health_insurance"
            :form="form"
            label="employee.employment.health_insurance"
            value="employment.has_health_insurance"
        />

        <EmployeeOptionalInput
            v-if="company.employees_optional.danmarks_statistik"
            :form="form"
            label="employee.employment.danmarks_statistik"
            value="employment.danmarks_statistik"
        >
            <InputRow
                :form="form"
                name="dst_time_limited_employment"
                label="employee.employment.dst_time_limited_employment"
                error-key="employment.dst_time_limited_employment"
            >
                <Select
                    v-model="form.employment.dst_time_limited_employment"
                    :options="dstValuesOptions"
                />
            </InputRow>

            <InputRow
                :form="form"
                name="disco_code"
                label="employee.employment.disco_code"
                error-key="employment.disco_code"
            >
                <Combobox
                    v-model="form.employment.disco_code"
                    :options="discoCodesOptions"
                    :show-remove-button="false"
                    position="popper"
                />
            </InputRow>

            <InputRow
                :form="form"
                name="dst_hired_terms"
                label="employee.employment.dst_hired_terms"
                error-key="employment.dst_hired_terms"
            >
                <Select
                    v-model="form.employment.dst_hired_terms"
                    :options="hiredTermsOptions"
                />
            </InputRow>

            <InputRow
                :form="form"
                name="dst_job_status"
                label="employee.employment.dst_job_status"
                error-key="employment.dst_job_status"
            >
                <Select
                    v-model="form.employment.dst_job_status"
                    :options="jobStatusesOptions"
                />
            </InputRow>
            <InputRow
                :form="form"
                name="dst_salary_form"
                label="employee.employment.dst_salary_form"
                error-key="employment.dst_salary_form"
            >
                <Select
                    v-model="form.employment.dst_salary_form"
                    :options="salaryFormsOptions"
                />
            </InputRow>
        </EmployeeOptionalInput>
    </div>
</template>
